import { Divider } from '@fluentui/react-components';
import { DeleteRegular, EditRegular } from '@fluentui/react-icons';
import {
  ClassService,
  UserListSorting,
  getSortedUserLabel,
  sortUsersByPreference,
} from '@mnspro-cloud/services';
import cn from 'classnames';
import moment from 'moment-timezone';
import React, { FC } from 'react';

import { Button } from '../../../Button';
import {
  CommunicationMessage,
  Illustrations,
  IllustrationSize,
} from '../../../Communication';
import { ThemeMode, useProviderTheme } from '../../../hooks/useProviderTheme';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';
import '../Homework/HomeworkListingDialog.scss';
import { ClassServiceListItem } from './ClassServiceListItem';
import './ClassServicesListingDialog.scss';
import './NewClassServiceDialog.scss';

export interface ClassServicesListingDialogProps {
  isMobileView: boolean;
  labels: ClassServicesListingDialogLabels;
  open: boolean;
  classServices: ClassService[];
  isLoading?: boolean;
  onCloseClick?: () => void;
  deleteClick?: (service: ClassService) => void;
  editClick?: (service: ClassService) => void;
  disabled: boolean;
  preferenceSorting?: UserListSorting;
}

export interface ClassServicesListingDialogLabels {
  header: string;
  closeButtonLabel: string;
  noServicesHeader: string;
  noServicesContent: string;
  inactiveServicesLabel: string;
  pastServicesLabel: string;
  currentServicesLabel: string;
  futureServicesLabel: string;
}

const addDivider = (list: JSX.Element[]): JSX.Element[] => {
  const divider = <Divider className="cardDivider" key="divider" />;
  const newList: JSX.Element[] = [];
  let counter = 0;
  for (const item of list) {
    newList.push({ ...item, key: String(counter) });
    if (counter === list.length - 1) {
      break;
    }
    newList.push({ ...divider, key: `divider-${counter}` });
    counter++;
  }
  return newList;
};

export const ClassServicesListingDialog: FC<
  ClassServicesListingDialogProps
> = props => {
  const {
    isMobileView,
    open,
    labels,
    classServices,
    isLoading,
    onCloseClick,
    deleteClick,
    editClick,
    disabled,
    preferenceSorting,
  } = props;
  const { mode } = useProviderTheme();
  const today = moment(new Date());

  const createClassServiceCard = (service: ClassService, index: number) => (
    <ClassServiceListItem
      key={index}
      service={{
        id: service.id,
        start: service.start,
        end: service.end,
        content: service.content,
        title: service.title,
        students: sortUsersByPreference(
          service.students,
          preferenceSorting ?? UserListSorting.FirstName,
        ).map(student =>
          getSortedUserLabel(student, preferenceSorting ?? UserListSorting.FirstName),
        ),
      }}
      actions={
        disabled
          ? []
          : [
              { icon: <EditRegular />, onClick: () => editClick?.(service) },
              { icon: <DeleteRegular />, onClick: () => deleteClick?.(service) },
            ]
      }
    />
  );

  const currentServices = [];

  const futureServices = [];

  const pastServices = [];

  const inactiveServices = [];
  let counter = 0;
  for (const service of classServices) {
    const component = createClassServiceCard(service, counter);

    if (!service.isActive) {
      //inactive
      inactiveServices.push(component);
    } else if (today.isBetween(service.start, service.end, 'd', '[]')) {
      //current
      currentServices.push(component);
    } else if (today.isBefore(service.start)) {
      //future
      futureServices.push(component);
    } else if (today.isAfter(service.end)) {
      //past
      pastServices.push(component);
    }
    counter++;
  }

  const classServicesComponent =
    classServices.length > 0 ? (
      <div
        role="classServicesList"
        className={cn(
          'flex flex-col items-stretch serviceListWrapper',
          { content: !isMobileView },
        )}
      >
        {currentServices.length > 0 && (
          <>
            <div className="divider">{labels.currentServicesLabel}</div>
            <ul className="serviceList">{addDivider(currentServices)}</ul>
          </>
        )}
        {futureServices.length > 0 && (
          <>
            <div className="divider">{labels.futureServicesLabel}</div>
            <ul className="serviceList">{addDivider(futureServices)}</ul>
          </>
        )}
        {pastServices.length > 0 && (
          <>
            <div className="divider">{labels.pastServicesLabel}</div>
            <ul className="serviceList">{addDivider(pastServices)}</ul>
          </>
        )}
        {inactiveServices.length > 0 && (
          <>
            <div className="divider">{labels.inactiveServicesLabel}</div>
            <ul className="serviceList">{addDivider(inactiveServices)}</ul>
          </>
        )}
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center emptyMsg">
        <CommunicationMessage
          illustration={Illustrations.Empty}
          headline={labels.noServicesHeader}
          text={labels.noServicesContent}
          useDarkIllustration={mode === ThemeMode.Dark}
          illustrationSize={IllustrationSize.Small}
        />
      </div>
    );

  return (
    <ResponsiveDialog
      open={open}
      header={labels.header}
      className="classServiceListingDialog"
      secondaryAction={
        <Button onClick={onCloseClick}>{labels.closeButtonLabel}</Button>
      }
    >
      {classServicesComponent}
    </ResponsiveDialog>
  );
};
